/**
 * AtlasPanelWindowsModule — windowizes the classic SIDE PANELS (the index.html
 * docks: Passport kiosk, Compass, Orbits, Forge Canvas, World Logic) into
 * SWG-style draggable/resizable windows, with ZERO changes to their modules.
 *
 * Those panels all share one contract: a static element in index.html whose
 * visibility is a `.show` class the owning module toggles. adoptWindow() wraps
 * each in window chrome and mirrors that class onto the window, so the C/O/G/
 * L/P hotkeys, the menu bar, each panel's own close buttons, and the window ×
 * all stay in perfect agreement — and every window remembers its position,
 * size, roll-up, and opacity like the rest of the UI.
 *
 * Deliberately NOT windowized: #pulse (the always-on HUD, pointer-events:none),
 * #login (a modal gate), and the fullscreen overlays (Help, Graphics options).
 */

import type { App, IModule } from 'enginex';
import { adoptWindow } from '../lib/ui_window.js';

interface Dock {
  sel: string;
  id: string;
  title: string;
  icon: string;
  width: number;
  height: number;
  right?: boolean; // default x from the right edge
}

const DOCKS: Dock[] = [
  { sel: '#passport', id: 'passport', title: 'Passport', icon: 'passport', width: 420, height: 620, right: true },
  { sel: '#compass', id: 'compass', title: 'Social Compass', icon: 'compass', width: 390, height: 560 },
  { sel: '#orbit', id: 'orbit', title: 'Orbits', icon: 'orbit', width: 390, height: 480, right: true },
  { sel: '#forge', id: 'forge-canvas', title: 'Forge Canvas', icon: 'forge', width: 420, height: 600, right: true },
  { sel: '#worldlogic', id: 'world-logic', title: 'World Logic', icon: 'logic', width: 440, height: 620 },
];

export class AtlasPanelWindowsModule implements IModule {
  readonly name = 'AtlasPanelWindows';

  init(_app: App): void {
    void _app;
    // The docks are static index.html elements — they exist before any module
    // init runs, so adoption is safe here (their modules keep their references:
    // the nodes MOVE into the window bodies, getElementById still finds them).
    for (const d of DOCKS) {
      const el = document.querySelector<HTMLElement>(d.sel);
      if (!el) continue;
      adoptWindow(el, {
        id: d.id,
        title: d.title,
        icon: d.icon,
        width: d.width,
        height: d.height,
        minWidth: 320,
        minHeight: 220,
        x: d.right ? Math.max(12, window.innerWidth - d.width - 20) : 12,
        y: 56,
        isOpen: (e) => e.classList.contains('show'),
        close: (e) => e.classList.remove('show'),
      });
    }
  }
}
